import { useQuery } from 'react-query';
import { communicationKeys } from '../cache-keys/communicationKeys';
import { useAPI } from '@axo/shared/data-access/provider';
import { comment_api } from '@axo/shared/data-access/api';
import { comment } from '@axo/shared/data-access/types';

/**
 * Fetches comments via `GET /comment`
 * (services/comment/cmd/lambda/main.go:55).
 *
 * Supports filtering by `ID`, `LoanApplicationID`, `OwnerID`, and
 * `FollowupAtInMinutes`. The query is disabled until at least one filter
 * is provided (the service rejects empty queries).
 *
 * Use `FollowupAtInMinutes` together with `OwnerID` to fetch the upcoming
 * follow-up notifications for a given owner.
 */
export function useComments(params: comment.FindCommentsParams) {
  const api = useAPI();

  // Keep the existing communication-keyed prefix so prefix-based
  // invalidations (e.g. communicationKeys.comments({ applicationID })) keep
  // working across all filter combinations for the same application.
  const cacheKey = [
    ...communicationKeys.comments({ applicationID: params.LoanApplicationID }),
    params,
  ] as const;

  const hasFilter =
    params.ID !== undefined ||
    params.LoanApplicationID !== undefined ||
    params.OwnerID !== undefined ||
    params.FollowupAtInMinutes !== undefined;

  return useQuery(cacheKey, () => comment_api.find(api)(params), {
    enabled: hasFilter,
    refetchInterval: 5 * 60 * 1000,
  });
}
